import { ScrollArea } from '@neo/primitives/scroll-area'
import { Sheet, SheetContent } from '@neo/primitives/sheet'
import { Skeleton } from '@neo/primitives/skeleton'
import type { McpArticle } from 'v2/pages/flow/knowledge-base/types'
import { useFetchDocumentFromSource } from '../../../queries'
import { DocumentContent } from '../document-content'

interface DocumentPreviewSheetProps {
  isOpen: boolean
  onClose: () => void
  document: McpArticle | null
  source?: string
  sourceId?: string
  documentId?: string
}

// Loading skeleton component for document preview
const DocumentPreviewSkeleton = () => (
  <div className="flex flex-col p-6">
    <div className="flex justify-between items-start mb-4">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-4 w-48" />
      </div>
    </div>
    <div className="flex flex-col gap-4">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-4 w-4/5" />
      <div className="flex flex-col gap-2 mt-4">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-2/3" />
      </div>
      <div className="flex flex-col gap-2 mt-4">
        <Skeleton className="h-4 w-4/5" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/5" />
      </div>
      <div className="flex flex-col gap-2 mt-4">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-4/6" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-2/5" />
      </div>
    </div>
  </div>
)

export const DocumentPreviewSheet = ({
  isOpen,
  onClose,
  document,
  source,
  sourceId,
  documentId,
}: DocumentPreviewSheetProps) => {
  // Fetch document if not provided but we have source and documentId
  const shouldFetch = !document?.content && !!source && !!documentId
  const { data: documentData, isLoading } = useFetchDocumentFromSource(
    shouldFetch ? source : '',
    shouldFetch ? sourceId || '' : '',
    shouldFetch ? documentId || '' : '',
  )

  // Get the document either from props or from fetch result
  const displayDocument = document?.content ? document : documentData?.data

  if (isLoading) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent className="w-[450px] sm:w-[450px] md:w-[450px] lg:max-w-[450px] flex flex-col p-0">
          <DocumentPreviewSkeleton />
        </SheetContent>
      </Sheet>
    )
  }

  if (!displayDocument) return null

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[450px] sm:w-[450px] md:w-[450px] lg:max-w-[450px] flex flex-col p-0">
        <div className="flex flex-col p-6">
          <div className="flex justify-between items-start mb-4">
            <div className="flex flex-col">
              <h2 className="text-h3-500 text-foreground">Page preview</h2>
              <p className="text-s-400 text-secondary">{displayDocument.title}</p>
            </div>
          </div>
          <ScrollArea className="h-[calc(100vh-120px)]">
            <div className="flex flex-col gap-4">
              <DocumentContent
                content={displayDocument.content}
                isHtml={displayDocument.content_type === 'html'}
              />
            </div>
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  )
}
